import { STORAGE_NAMES } from '@static/consts/consts'

/**
 * @description SSO-переход из виджета в бэк-офис.
 *
 * widget-v2, встроенный в client-onepage-callcenter, открывает бэк-офис ссылкой
 * `${crmUrl}?token=<JWT>`. Токен выпущен колл-центром, а не scrm, поэтому куки
 * `AccessToken` на домене бэк-офиса у такого оператора нет — и куки-режим
 * (`VITE_COOKIE_AUTH_ENABLED=true`, на котором работают инстансы Mexico) увёл бы его
 * на логин LMS. Поэтому токен, пришедший из query, побеждает куки-режим: пока он
 * лежит в localStorage, запросы уходят с `Authorization: Bearer`.
 *
 * `STORAGE_NAMES` импортируется явно, а не берётся из одноимённого глобала: этот модуль
 * работает раньше всех остальных (см. `adoptTokenFromLocation`), и зависеть от того, успел
 * ли `@static/consts/index` зарегистрировать глобалы на `window`, здесь нельзя.
 */

/**
 * @description Отсекает «токены», которых на самом деле нет.
 *
 * Ссылки виджета собираются шаблонной строкой `?token=${ props.token }`, поэтому на хостах,
 * где токен не передаётся (в бэк-офисе LMS виджет ходит под кукой scrm), в query приезжает
 * литерал `"undefined"`. Принять его за токен — значит уйти в Bearer-режим без куки,
 * получить 401 и уехать на логин LMS, который вернёт на ту же ссылку: замкнутый круг.
 *
 * Проверка применяется и к уже сохранённому значению, поэтому localStorage, испорченный
 * до этого фикса, чинится сам при следующей загрузке.
 */
export const isUsableToken = ( value: unknown ): value is string =>
  typeof value === 'string' && value !== '' && value !== 'undefined' && value !== 'null'

const isTokenPresent = () => isUsableToken( localStorage.getItem( STORAGE_NAMES.AUTH_TOKEN ) )

/**
 * @description Уход на другой хост уже запущен.
 *
 * 401 прилетает сразу по нескольким параллельным запросам. Без этого флага первый из них
 * увёл бы оператора в колл-центр, а следующие — уже со сброшенной внешней авторизацией —
 * попали бы в куки-ветку и перебили редирект логином LMS.
 */
let isLeaving = false

/** @description Помечает вкладку как авторизованную внешним токеном (из `?token=`) */
export const markExternalAuth = () => {
  localStorage.setItem( STORAGE_NAMES.EXTERNAL_AUTH, 'true' )
}

/**
 * @description Снимает токен из `?token=` ДО старта приложения.
 *
 * MEXC-49574. Раньше это делал навигационный guard `extractGuardFromQueries` — и опаздывал
 * всегда, а не иногда. `app.use( router )` только запускает начальную навигацию, первый guard
 * вызывается уже из микротаски, а `app.mount( '#app' )` отрабатывает синхронно следом: `App.vue`
 * в `setup` безусловно дёргает `getCrmAppSettings()` -> `GET /SettingGroups/2/Settings`. На момент
 * отправки этого запроса localStorage ещё пуст, значит `isCookieAuth()` -> true, значит уходит
 * `Authorization: ''` + `credentials: 'include'`. У оператора колл-центра куки scrm на домене
 * бэк-офиса нет -> 401 -> `authMiddleware` уводит на логин LMS, и токен не читается вообще никогда.
 *
 * Поэтому внешний токен — вход времени загрузки, а не времени навигации. Гейтить boot-запросы
 * на `router.isReady()` не годится: любой новый запрос на старте ломал бы сценарий заново.
 *
 * Guard остаётся — он чистит `?token=` из истории; повторная запись там безвредна.
 *
 * @param search строка запроса; параметром — чтобы тест не подменял `window.location`.
 */
export const adoptTokenFromLocation = ( search: string = window.location.search ) => {
  const token = new URLSearchParams( search ).get( 'token' )

  if ( !isUsableToken( token ) ) {
    return
  }

  localStorage.setItem( STORAGE_NAMES.AUTH_TOKEN, token )
  markExternalAuth()
}

/** @description Сбрасывает внешнюю авторизацию — вызывается, когда токен протух (401) */
export const clearExternalAuth = () => {
  localStorage.removeItem( STORAGE_NAMES.EXTERNAL_AUTH )
  localStorage.removeItem( STORAGE_NAMES.AUTH_TOKEN )
}

/** @description Авторизованы токеном, полученным при переходе из виджета */
export const isExternalAuth = () => localStorage.getItem( STORAGE_NAMES.EXTERNAL_AUTH ) === 'true' && isTokenPresent()

/**
 * @description Единственная точка, определяющая режим авторизации в рантайме.
 * Собранный куки-режим действует, только пока нет внешнего токена.
 */
export const isCookieAuth = () => import.meta.env.VITE_COOKIE_AUTH_ENABLED === 'true' && !isExternalAuth()

/**
 * @description Уводит на хост, который выдал текущую авторизацию, чтобы получить новую.
 *
 * Токен колл-центра живёт фиксированные 8 часов и не обновляется: httpOnly-кука с ним
 * лежит на домене колл-центра, и бэк-офис не может её прочитать через CORS. Поэтому
 * единственный путь за новым токеном — вернуть оператора в колл-центр, где он снова
 * перейдёт по ссылке виджета.
 *
 * @returns `false`, если внешней авторизации нет или `VITE_CALLCENTER_AUTH_URL` не задан —
 * тогда вызывающий код отрабатывает штатный сценарий (куки-режим или UnauthorizedPage),
 * а уже выданный токен остаётся на месте: заменить его всё равно нечем.
 */
export const redirectToCallcenter = (): boolean => {
  if ( isLeaving ) {
    return true
  }

  if ( !isExternalAuth() ) {
    return false
  }

  const targetUrl = import.meta.env.VITE_CALLCENTER_AUTH_URL

  // Сбрасываем внешнюю авторизацию только когда действительно уходим за новым токеном.
  // Раньше `clearExternalAuth()` стоял выше этого bail'а, и на инстансе без
  // `VITE_CALLCENTER_AUTH_URL` любой 401 — по любому запросу — стирал единственный токен,
  // после чего `isCookieAuth()` снова становился true и куки-ветка уводила на логин LMS.
  if ( !targetUrl ) {
    return false
  }

  clearExternalAuth()

  isLeaving = true
  window.location.assign( targetUrl )
  return true
}
